// Microphone recorder -> 16 kHz mono 16-bit WAV (base64), for /api/stt, /api/stt/public, /api/chat audio.
// Capture: AudioWorklet when available, ScriptProcessorNode otherwise (older iOS WebViews). Works in iOS WKWebView
// (14.3+), Android WebView (RECORD_AUDIO granted by the shell), Chrome and Electron.
// iOS needs the AudioContext resumed inside a user gesture: call primeAudio() from pointerdown/click first.
import { barsOf, concat, downsample, encodeWav, rms, toBase64 } from './wav';
import type { AudioPayload } from '../api/types';

/** `levels`: the clip's loudness as 20 bars, 0..1 (loudest = 1), for the voice bubble in chat. */
export interface Clip extends AudioPayload { mime: 'audio/wav'; ms: number; blob: Blob; levels: number[] }
export type RecorderErrorKind = 'denied' | 'no-mic' | 'unsupported' | 'failed';
export class RecorderError extends Error { constructor(public kind: RecorderErrorKind, message: string) { super(message); this.name = 'RecorderError'; } }

export const TARGET_RATE = 16000;
export const MAX_MS = 60000;

type Ctx = AudioContext;
let ctx: Ctx | null = null;
let workletReady: Promise<boolean> | null = null;

function makeContext(): Ctx | null {
  const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const K = W.AudioContext || W.webkitAudioContext;
  if (!K) return null;
  try { return new K({ latencyHint: 'interactive' }); } catch { try { return new K(); } catch { return null; } }
}

/** Create/resume the shared AudioContext. Call synchronously inside a user gesture (pointerdown, click). */
export function primeAudio(): void {
  if (!ctx || ctx.state === 'closed') { ctx = makeContext(); workletReady = null; }
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
}

export function canRecord(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!makeContextCtor();
}
function makeContextCtor() {
  const W = window as unknown as { AudioContext?: unknown; webkitAudioContext?: unknown };
  return W.AudioContext || W.webkitAudioContext;
}

const WORKLET = `
class XmCapture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.n++] = ch[i];
        if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor('xm-capture', XmCapture);
`;

function loadWorklet(c: Ctx): Promise<boolean> {
  if (!workletReady) {
    workletReady = (async () => {
      if (!c.audioWorklet || typeof AudioWorkletNode === 'undefined') return false;
      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
      try { await c.audioWorklet.addModule(url); return true; } catch { return false; } finally { URL.revokeObjectURL(url); }
    })();
  }
  return workletReady;
}

export class Recorder {
  /** Called ~10x per second with the input level 0..1 while recording. */
  onLevel: ((level: number) => void) | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioNode | null = null;
  private sink: GainNode | null = null;
  private chunks: Float32Array[] = [];
  private loud: number[] = [];
  private rate = 48000;
  private t0 = 0;
  private active = false;

  get recording() { return this.active; }
  get elapsed() { return this.active ? Date.now() - this.t0 : 0; }

  async start(): Promise<void> {
    if (this.active) return;
    if (!navigator.mediaDevices?.getUserMedia) throw new RecorderError('unsupported', 'This device cannot record here.');
    primeAudio();
    const c = ctx;
    if (!c) throw new RecorderError('unsupported', 'This device cannot record here.');
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (e) {
      const n = (e as DOMException)?.name;
      if (n === 'NotAllowedError' || n === 'SecurityError' || n === 'PermissionDeniedError') throw new RecorderError('denied', 'Microphone access is off.');
      if (n === 'NotFoundError' || n === 'OverconstrainedError' || n === 'DevicesNotFoundError') throw new RecorderError('no-mic', 'No microphone found.');
      throw new RecorderError('failed', 'Could not start the microphone.');
    }
    if (c.state === 'suspended') { try { await c.resume(); } catch { /* keep going */ } }
    this.rate = c.sampleRate;
    this.chunks = [];
    this.loud = [];
    this.source = c.createMediaStreamSource(this.stream);
    this.sink = c.createGain();
    this.sink.gain.value = 0;
    const onFrame = (f: Float32Array) => {
      if (!this.active) return;
      this.chunks.push(f);
      const lv = rms(f);
      this.loud.push(lv);
      this.onLevel?.(Math.min(1, lv * 4));
    };
    if (await loadWorklet(c)) {
      const n = new AudioWorkletNode(c, 'xm-capture', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 });
      n.port.onmessage = (ev: MessageEvent<Float32Array>) => onFrame(ev.data);
      this.node = n;
    } else {
      const sp = c.createScriptProcessor(4096, 1, 1);
      sp.onaudioprocess = (ev) => onFrame(new Float32Array(ev.inputBuffer.getChannelData(0)));
      this.node = sp;
    }
    this.source.connect(this.node);
    this.node.connect(this.sink);
    this.sink.connect(c.destination);
    this.t0 = Date.now();
    this.active = true;
  }

  /** Stop and return the clip (16 kHz mono WAV). */
  async stop(): Promise<Clip> {
    const ms = this.elapsed;
    const rate = this.rate;
    // Let the last worklet buffer arrive.
    await new Promise((r) => setTimeout(r, 60));
    const chunks = this.chunks;
    const levels = barsOf(this.loud);
    this.teardown();
    const pcm = downsample(concat(chunks), rate, TARGET_RATE);
    const wav = encodeWav(pcm, TARGET_RATE);
    return { b64: toBase64(wav), mime: 'audio/wav', ms, blob: new Blob([wav], { type: 'audio/wav' }), levels };
  }

  cancel(): void { this.teardown(); }

  private teardown() {
    this.active = false;
    try { this.source?.disconnect(); } catch { /* ignore */ }
    try { this.node?.disconnect(); } catch { /* ignore */ }
    try { this.sink?.disconnect(); } catch { /* ignore */ }
    if (this.node && 'port' in this.node) (this.node as AudioWorkletNode).port.onmessage = null;
    if (this.node && 'onaudioprocess' in this.node) (this.node as ScriptProcessorNode).onaudioprocess = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null; this.source = null; this.node = null; this.sink = null; this.chunks = []; this.loud = [];
    // Release the audio session (iOS lowers media volume while a mic session is open).
    if (ctx && ctx.state === 'running') void ctx.suspend().catch(() => {});
  }
}
